import patientApi from '@/api/_patient'

const texte = value => {
    if (typeof value === 'string') return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    return ''
}

const non_renseigne = 'Non renseigné'

export default {
    name: 'patient-profile-basic',
    props: {
        patient: { type: Object, default: () => ({}) },
        user_role: String,
        consultation: { type: Object, default: () => ({}) },
        info_personne_a_prevenir: { type: Object, default: () => ({}) },
        info_assure_principal: { type: Object, default: () => ({}) },
        assurance_loading: { type: Boolean, default: false },
        assurance_erreur: { type: Boolean, default: false }
    },
    data() {
        return {
            groupe_sanguin_selection: '',
            groupe_sanguin_enregistrement: false,
            groupe_sanguin_erreur: '',
            groupes_sanguins: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
        }
    },
    computed: {
        patient_data() { return this.patient || {} },
        consultation_data() { return this.consultation || {} },
        assurance_data() { return this.info_assure_principal || {} },
        nom_complet() {
            return [texte(this.patient_data.nom).toUpperCase(), texte(this.patient_data.prenoms)]
                .filter(Boolean).join(' ') || non_renseigne
        },
        numero_dossier() { return texte(this.patient_data.code) || non_renseigne },
        taille_libelle() { return this.libelleMesure('taille', ['cm', 'm']) },
        poids_libelle() { return this.libelleMesure('poids', ['kg', 'g']) },
        // IMC (kg/m²) juste après le poids (04/10/2026) ; absent si la taille ou le poids manque.
        imc_libelle() {
            const taille = this.valeurMesure('taille'), poids = this.valeurMesure('poids')
            const metres = taille.unite === 'cm' ? taille.nombre / 100 : taille.unite === 'm' ? taille.nombre : 0
            const kilos = poids.unite === 'kg' ? poids.nombre : poids.unite === 'g' ? poids.nombre / 1000 : 0
            if (!metres || !kilos) return ''
            return (kilos / (metres * metres)).toFixed(1).replace('.', ',') + ' kg/m²'
        },
        groupe_sanguin_libelle() { return texte(this.patient_data.groupe_sanguin_declare) || non_renseigne },
        sexe_libelle() {
            const sexe = this.patient_data.sexe || {}
            return this.formatSexeCourt(sexe, non_renseigne)
        },
        avatar_feminin() {
            const sexe = this.patient_data.sexe || {}
            return [sexe.specificatif, sexe.description].some(value =>
                ['féminin', 'feminin', 'femme'].includes(texte(value).toLowerCase()))
        },
        age_libelle() { return texte(this.patient_data.age) || non_renseigne },
        telephone_contact() {
            return texte((this.info_personne_a_prevenir || {}).personne_a_prevenir_telephone)
        },
        telephone_personne_a_prevenir() {
            return !texte(this.patient_data.telephone_mobile) && Boolean(this.telephone_contact)
        },
        telephone_principal() {
            return texte(this.patient_data.telephone_mobile) || this.telephone_contact || non_renseigne
        },
        telephone_secondaire() { return texte(this.patient_data.telephone_secondaire) },
        type_consultation() {
            const tarif = this.consultation_data.tarif_consultation || {}
            return texte((tarif.type_consultation || {}).nom) || non_renseigne
        },
        medecin_traitant() {
            if (Number(this.consultation_data.is_start) !== 1 && Number(this.consultation_data.is_end) !== 1) return 'Non attribué'
            const medecin = this.consultation_data.medecin
            if (typeof medecin === 'string') {
                try {
                    const identite = JSON.parse(medecin)
                    return texte(identite.user || identite.nom_medecin || identite.nom) || non_renseigne
                } catch (error) { return non_renseigne }
            }
            return texte((medecin || {}).user || (medecin || {}).nom_medecin || (medecin || {}).nom) || non_renseigne
        },
        employeur() {
            return texte(((this.patient_data.societe || {}).societe || {}).nom)
        },
        departement() {
            return texte(((this.patient_data.societe || {}).departement || {}).libelle)
        },
        est_assure() {
            if (this.assurance_loading || this.assurance_erreur || this.assurance_data.est_assure === undefined) {
                return Number(this.patient_data.est_assure) === 1
            }
            return Number(this.assurance_data.est_assure) === 1
        },
        assurance_disponible() {
            return this.est_assure && !this.assurance_loading && !this.assurance_erreur
                && this.assurance_data.est_assure !== undefined
        },
        compagnie_assurance() { return texte(this.assurance_data.assurance) || non_renseigne },
        societe_souscriptrice() {
            // This field is resolved through the same subscriber as the company and rate.
            return texte(this.assurance_data.societe_souscriptrice) || non_renseigne
        },
        taux_general() {
            const valeur = this.assurance_data.taux_general
            if (typeof valeur !== 'number' && typeof valeur !== 'string') return non_renseigne
            if (typeof valeur === 'string' && !/^\d+(?:\.\d+)?$/.test(valeur.trim())) return non_renseigne
            const fraction = Number(valeur)
            if (!Number.isFinite(fraction) || fraction < 0 || fraction > 1) return non_renseigne
            return (fraction * 100).toLocaleString('fr-FR', { maximumFractionDigits: 10 }) + ' %'
        },
        matricule_assure() { return texte(this.assurance_data.assure_matricule) || non_renseigne },
        assure_principal() {
            return [texte(this.assurance_data.assure_nom), texte(this.assurance_data.assure_prenom)]
                .filter(Boolean).join(' ')
        }
    },
    methods: {
        ouvrirGroupeSanguin() {
            this.groupe_sanguin_selection = this.groupes_sanguins.includes(this.groupe_sanguin_libelle)
                ? this.groupe_sanguin_libelle : ''
            this.groupe_sanguin_erreur = ''
            this.$refs.groupeSanguinModal.show()
        },
        async enregistrerGroupeSanguin() {
            if (!this.groupes_sanguins.includes(this.groupe_sanguin_selection) || !Number(this.patient_data.id)) return
            this.groupe_sanguin_enregistrement = true
            this.groupe_sanguin_erreur = ''
            try {
                await patientApi.updateDeclaredBloodGroup(this.patient_data.id, this.groupe_sanguin_selection)
                this.$emit('groupe-sanguin-enregistre')
                this.$refs.groupeSanguinModal.hide()
            } catch (error) {
                this.groupe_sanguin_erreur = 'Enregistrement impossible. Vérifiez votre connexion puis réessayez.'
            } finally {
                this.groupe_sanguin_enregistrement = false
            }
        },
        valeurMesure(champ) {
            const mesure = (this.patient_data.dernieres_mesures || {})[champ] || {}
            const valeur = texte(mesure.valeur).replace(',', '.')
            return { nombre: /^\d+(?:\.\d+)?$/.test(valeur) ? Number(valeur) : 0, unite: texte(mesure.unite) }
        },
        libelleMesure(champ, unites) {
            const mesure = (this.patient_data.dernieres_mesures || {})[champ] || {}
            const valeur = texte(mesure.valeur).replace(',', '.')
            const unite = texte(mesure.unite)
            return /^\d+(?:\.\d+)?$/.test(valeur) && Number(valeur) > 0 && unites.includes(unite)
                ? valeur.replace('.', ',') + ' ' + unite : non_renseigne
        }
    }
}
